// // src/errors/handlers/axios-error.handler.ts

// /**
//  * Axios Error Handler
//  *
//  * WHY: Transform axios errors into typed application errors
//  * WHAT: Central handler for all axios/HTTP errors
//  * BENEFIT:
//  *   - Consistent error types from HTTP requests
//  *   - Automatic categorization and severity
//  *   - Proper error class instantiation
//  *
//  * DESIGN DECISIONS:
//  * 1. Factory pattern for creating appropriate error type
//  * 2. Preserves original error as cause
//  * 3. Extracts all available context
//  * 4. Handles edge cases (no response, timeout, etc.)
//  */

// import { AxiosError } from "axios";
// import {
//   AppError,
//   ApiError,
//   NetworkError,
//   TimeoutError,
//   CancellationError,
//   AuthenticationError,
//   AuthorizationError,
//   RateLimitError,
// } from "../classes";
// import type { ErrorContext } from "../types";
// import {
//   type ApiErrorResponse,
//   isApiErrorResponse,
// } from "../types/api-response.types";
// import {
//   isAxiosError,
//   isNetworkError,
//   isTimeoutError,
//   isCancelledError,
//   extractRetryAfter,
// } from "../utils/error-parser";
// import { errorLogger } from "../utils/error-logger";
// import { logger } from "@/core/utils";

// /**
//  * Options for handling axios errors
//  */
// export interface AxiosErrorHandlerOptions {
//   /** Additional context to include */
//   context?: Partial<ErrorContext>;
//   /** Log the error (default: true) */
//   log?: boolean;
// }

// /**
//  * Axios Error Handler Class
//  *
//  * Transforms axios errors into appropriate AppError subclasses.
//  */
// export class AxiosErrorHandler {
//   /**
//    * Handle any error and return appropriate AppError
//    *
//    * @param error - Any error (axios or other)
//    * @param options - Handler options
//    * @returns Appropriate AppError subclass
//    */
//   static handle(
//     error: unknown,
//     options: AxiosErrorHandlerOptions = {},
//   ): AppError {
//     console.log("error in handle", error);
//     const { context, log = true } = options;

//     // Already an AppError, just add context if provided
//     if (error instanceof AppError) {
//       if (context) {
//         return error.withContext(context);
//       }
//       return error;
//     }

//     // Handle axios errors
//     if (isAxiosError(error)) {
//       const appError = this.handleAxiosError(
//         error as AxiosError<ApiErrorResponse>,
//         context,
//       );

//       if (log) {
//         errorLogger.logError(appError.serialize());
//       }

//       return appError;
//     }

//     // Handle native errors
//     if (error instanceof Error) {
//       return new AppError(error.message, {
//         cause: error,
//         context: context ? { ...context, timestamp: new Date() } : undefined,
//       });
//     }

//     // Handle unknown errors
//     return new AppError(
//       typeof error === "string" ? error : "An unknown error occurred",
//       {
//         cause: error,
//         context: context ? { ...context, timestamp: new Date() } : undefined,
//       },
//     );
//   }

//   /**
//    * Handle axios-specific errors
//    */
//   private static handleAxiosError(
//     error: AxiosError<ApiErrorResponse>,
//     context?: Partial<ErrorContext>,
//   ): AppError {
//     logger.info("Axios Error", error);
//     // Build error context from request
//     const errorContext: ErrorContext = {
//       ...context,
//       url: error.config?.url,
//       method: error.config?.method?.toUpperCase(),
//       requestId: error.config?.headers?.["X-Request-ID"] as string | undefined,
//       timestamp: new Date(),
//     };

//     // Handle cancelled requests
//     if (isCancelledError(error)) {
//       return new CancellationError(undefined, {
//         context: errorContext,
//         cause: error,
//       });
//     }

//     // Handle timeout
//     if (isTimeoutError(error)) {
//       return TimeoutError.fromAxiosTimeout(
//         {
//           url: error.config?.url,
//           timeout: error.config?.timeout,
//         },
//         error,
//       );
//     }

//     // Handle network errors (no response)
//     if (isNetworkError(error)) {
//       return NetworkError.fromErrorCode(error.code || "ERR_NETWORK", error);
//     }

//     // Handle response errors
//     if (error.response) {
//       return this.handleResponseError(error, errorContext);
//     }

//     // Fallback for unknown axios errors
//     return new NetworkError(error.message, {
//       cause: error,
//       context: errorContext,
//     });
//   }

//   /**
//    * Handle HTTP response errors (4xx, 5xx)
//    */
//   private static handleResponseError(
//     error: AxiosError<ApiErrorResponse>,
//     context: ErrorContext,
//   ): AppError {
//     const status = error.response?.status ?? 500;
//     const data = error.response?.data; // ✅ Safe optional chaining

//     logger.info("status in handleResponseError", status);

//     // Handle specific status codes
//     switch (status) {
//       case 401:
//         return this.handleAuthenticationError(error, data, context);

//       case 403:
//         return this.handleAuthorizationError(error, data, context);
//       case 409:
//         return this.handleAuthenticationError(error, data, context);
//       case 429:
//         return this.handleRateLimitError(error, context);

//       default:
//         return this.handleGenericApiError(error, data, status, context);
//     }
//   }

//   /**
//    * Handle 401 Unauthorized
//    */
//   private static handleAuthenticationError(
//     error: AxiosError<ApiErrorResponse>,
//     data: ApiErrorResponse | undefined,
//     context: ErrorContext,
//   ): AuthenticationError {
//     const message = data?.message || "Authentication required";
//     const code = data?.code;

//     // Check for token expiration
//     if (code === "TOKEN_EXPIRED" || code === "ERR_TOKEN_EXPIRED") {
//       // return AuthenticationError.tokenExpired(error);
//     }

//     // Check for invalid token
//     if (code === "TOKEN_INVALID" || code === "ERR_TOKEN_INVALID") {
//       //return AuthenticationError.tokenInvalid(error);
//     }

//     // Generic auth error
//     return new AuthenticationError(message, {
//       context,
//       cause: error,
//     });
//   }

//   /**
//    * Handle 403 Forbidden
//    */
//   private static handleAuthorizationError(
//     error: AxiosError<ApiErrorResponse>,
//     data: ApiErrorResponse | undefined,
//     context: ErrorContext,
//   ): AuthorizationError {
//     const message = data?.message || "Access denied";

//     return new AuthorizationError(message, {
//       context,
//       cause: error,
//     });
//   }

//   /**
//    * Handle 429 Rate Limit
//    */
//   private static handleRateLimitError(
//     error: AxiosError<ApiErrorResponse>,
//     context: ErrorContext,
//   ): RateLimitError {
//     const retryAfter = extractRetryAfter(error);

//     return new RateLimitError(undefined, {
//       retryAfterSeconds: retryAfter,
//       context,
//       cause: error,
//     });
//   }

//   /**
//    * Handle generic API errors
//    */
//   private static handleGenericApiError(
//     error: AxiosError<ApiErrorResponse>,
//     data: ApiErrorResponse | undefined,
//     status: number,
//     context: ErrorContext,
//   ): ApiError {
//     // Use API response if available
//     if (data && isApiErrorResponse(data)) {
//       return ApiError.fromResponse(data, error);
//     }

//     // Create from status code
//     return ApiError.fromStatus(status, error, context);
//   }
// }

// src/core/errors/handlers/axios-error.handler.ts

/**
 * Axios Error Handler - SIMPLIFIED
 *
 * WHY SIMPLIFIED:
 * Your original worked but had redundant type guards and
 * complex response parsing spread across multiple methods.
 *
 * This version:
 * 1. Single clear path for each error type
 * 2. Uses ApiError.fromAxiosError() for response parsing
 *    (parsing logic lives with the error class, not the handler)
 * 3. Clean switch statement for status codes
 */

// src/core/errors/handlers/axios-error.handler.ts

import { AxiosError } from "axios";
import {
  AppError,
  ApiError,
  NetworkError,
  TimeoutError,
  CancellationError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  ConflictError, // ✅ Import ConflictError
  isAppError,
} from "../classes";
import type { ErrorContext } from "../types";
import { errorLogger } from "../utils/error-logger";
import { extractRetryAfter } from "../utils/error-parser";
import { logger } from "@/core/utils";
import { isApiErrorResponse } from "../types/api-response.types";

export interface AxiosErrorHandlerOptions {
  context?: Partial<ErrorContext>;
  log?: boolean;
}

export class AxiosErrorHandler {
  static handle(
    error: unknown,
    options: AxiosErrorHandlerOptions = {},
  ): AppError {
    const { context, log = true } = options;

    if (isAppError(error)) {
      return context ? error.withContext(context) : error;
    }

    if (this.isAxiosError(error)) {
      const appError = this.handleAxiosError(error, context);
      if (log) {
        errorLogger.logError(appError.serialize());
      }
      return appError;
    }

    if (error instanceof Error) {
      return new AppError(error.message, {
        cause: error,
        context: context ? { ...context, timestamp: new Date() } : undefined,
      });
    }

    return new AppError(
      typeof error === "string" ? error : "An unknown error occurred",
      {
        cause: error,
        context: context ? { ...context, timestamp: new Date() } : undefined,
      },
    );
  }

  private static handleAxiosError(
    error: AxiosError,
    context?: Partial<ErrorContext>,
  ): AppError {
    const errorContext: ErrorContext = {
      ...context,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      requestId: error.config?.headers?.["X-Request-ID"] as string | undefined,
      timestamp: new Date(),
    };

    if (error.code === "ERR_CANCELED") {
      return new CancellationError(undefined, {
        context: errorContext,
        cause: error,
      });
    }

    if (error.code === "ECONNABORTED" || error.code === "ERR_TIMED_OUT") {
      return TimeoutError.fromAxiosTimeout(
        { url: error.config?.url, timeout: error.config?.timeout },
        error,
      );
    }

    if (!error.response) {
      return NetworkError.fromErrorCode(error.code || "ERR_NETWORK", error);
    }

    return this.handleResponseError(error, errorContext);
  }

  /**
   * Handle HTTP response errors by status code
   *
   * ✅ FIX: 409 now routes to handleConflictError, NOT handleAuthenticationError
   *
   * WHY THIS MAPPING MATTERS:
   * ┌────────┬──────────────────────┬────────────────────────────────┐
   * │ Status │ Error Class          │ What happens                   │
   * ├────────┼──────────────────────┼────────────────────────────────┤
   * │ 401    │ AuthenticationError  │ Token refresh → logout         │
   * │ 403    │ AuthorizationError   │ Show "access denied"           │
   * │ 409    │ ConflictError        │ Show field error on form ✅    │
   * │ 429    │ RateLimitError       │ Show "slow down" + retry-after │
   * │ 4xx    │ ApiError             │ Generic client error           │
   * │ 5xx    │ ApiError             │ Generic server error + retry   │
   * └────────┴──────────────────────┴────────────────────────────────┘
   */
  private static handleResponseError(
    error: AxiosError,
    context: ErrorContext,
  ): AppError {
    const status = error.response!.status;
    const responseData = error.response!.data;

    logger.debug(`Handling response error: ${status}`, {
      url: context.url,
    });

    switch (status) {
      case 401:
        return this.handleAuthenticationError(error, responseData, context);

      case 403:
        return this.handleAuthorizationError(error, responseData, context);

      // ✅ FIXED: 409 gets its own handler
      case 409:
        return this.handleConflictError(error, responseData, context);

      case 429:
        return this.handleRateLimitError(error, context);

      default:
        return this.handleGenericApiError(error, responseData, status, context);
    }
  }

  // ─── 401 Handler ──────────────────────────────────
  private static handleAuthenticationError(
    error: AxiosError,
    data: unknown,
    context: ErrorContext,
  ): AuthenticationError {
    const message = this.extractMessage(data) || "Authentication required";

    return new AuthenticationError(message, {
      context,
      cause: error,
    });
  }

  // ─── 403 Handler ──────────────────────────────────
  private static handleAuthorizationError(
    error: AxiosError,
    data: unknown,
    context: ErrorContext,
  ): AuthorizationError {
    const message = this.extractMessage(data) || "Access denied";

    return new AuthorizationError(message, {
      context,
      cause: error,
    });
  }

  // ─── 409 Handler ✅ NEW ───────────────────────────
  /**
   * Handle 409 Conflict
   *
   * WHY A DEDICATED HANDLER:
   * 409 errors carry specific metadata that other errors don't:
   * - Which field caused the conflict
   * - What value conflicted
   * - What the existing resource is
   *
   * This metadata enables the UI to:
   * - Highlight the specific form field
   * - Suggest alternatives (e.g., "Try logging in instead")
   * - Offer resolution actions
   *
   * BACKEND RESPONSE FORMATS HANDLED:
   *
   * Format 1 (Standard):
   * {
   *   success: false,
   *   error: {
   *     message: "Email already exists",
   *     errorCode: "CONFLICT",
   *     statusCode: 409,
   *     details: { field: "email", value: "john@example.com" }
   *   }
   * }
   *
   * Format 2 (Legacy):
   * {
   *   status: "fail",
   *   message: "Email already exists",
   *   code: "CONFLICT",
   *   errors: { email: ["Email already registered"] }
   * }
   *
   * Format 3 (Simple):
   * {
   *   message: "Duplicate entry"
   * }
   */
  private static handleConflictError(
    error: AxiosError,
    data: unknown,
    context: ErrorContext,
  ): ConflictError {
    const extracted = this.extractConflictInfo(data);

    return new ConflictError(extracted.message || "Resource already exists", {
      conflictField: extracted.conflictField,
      conflictValue: extracted.conflictValue,
      resourceType: extracted.resourceType,
      context,
      cause: error,
      validationErrors: extracted.validationErrors,
    });
  }

  /**
   * Extract conflict-specific information from response data
   *
   * Handles all your backend response formats and extracts:
   * - The human-readable message
   * - Which field conflicted
   * - What value conflicted
   * - Validation errors for form display
   */
  private static extractConflictInfo(data: unknown): {
    message?: string;
    conflictField?: string;
    conflictValue?: string;
    resourceType?: string;
    validationErrors?: Array<{ field: string; message: string }>;
  } {
    if (!data || typeof data !== "object") {
      return {};
    }

    // ─── Format 1: Standard backend response ─────
    if (isApiErrorResponse(data)) {
      const errorData = data.error;
      const details = errorData.details as Record<string, unknown> | undefined;

      // Extract field and value from details
      const conflictField = details?.field as string | undefined;
      const conflictValue = details?.value as string | undefined;
      const resourceType = details?.resourceType as string | undefined;

      // Build validation errors for form display
      const validationErrors: Array<{ field: string; message: string }> = [];
      if (conflictField) {
        validationErrors.push({
          field: conflictField,
          message: errorData.message,
        });
      }

      // Also check if backend sent validationErrors directly
      if (errorData.validationErrors) {
        validationErrors.push(...errorData.validationErrors);
      }

      return {
        message: errorData.message,
        conflictField,
        conflictValue,
        resourceType,
        validationErrors:
          validationErrors.length > 0 ? validationErrors : undefined,
      };
    }

    // ─── Format 3: Simple message ─────
    const d = data as Record<string, unknown>;
    if (typeof d.message === "string") {
      // Try to detect field from message
      const field = this.detectConflictFieldFromMessage(d.message);
      const validationErrors = field
        ? [{ field, message: d.message as string }]
        : undefined;

      return {
        message: d.message as string,
        conflictField: field,
        validationErrors,
      };
    }

    return {};
  }

  /**
   * Try to detect which field caused the conflict from the error message
   *
   * WHY:
   * Some backends only send a message like "Email already exists"
   * without structured field data. This heuristic extracts the field
   * so we can still highlight it in the form.
   *
   * This is a BEST EFFORT helper - structured responses are always preferred.
   */
  private static detectConflictFieldFromMessage(
    message: string,
  ): string | undefined {
    const lowerMessage = message.toLowerCase();

    const fieldPatterns: Record<string, string[]> = {
      email: ["email", "e-mail"],
      username: ["username", "user name", "login name"],
      phone: ["phone", "phone number", "mobile"],
      slug: ["slug", "url"],
      name: ["name already"],
      sku: ["sku"],
      code: ["code already", "coupon code"],
    };

    for (const [field, patterns] of Object.entries(fieldPatterns)) {
      if (patterns.some((pattern) => lowerMessage.includes(pattern))) {
        return field;
      }
    }

    return undefined;
  }

  // ─── 429 Handler ──────────────────────────────────
  private static handleRateLimitError(
    error: AxiosError,
    context: ErrorContext,
  ): RateLimitError {
    const retryAfter = extractRetryAfter(error);

    return new RateLimitError(undefined, {
      retryAfterSeconds: retryAfter,
      context,
      cause: error,
    });
  }

  // ─── Default Handler ─────────────────────────────
  private static handleGenericApiError(
    error: AxiosError,
    data: unknown,
    status: number,
    context: ErrorContext,
  ): ApiError {
    return ApiError.fromAxiosError(status, data, error, context);
  }

  // ─── Utilities ────────────────────────────────────
  private static extractMessage(data: unknown): string | undefined {
    if (!data || typeof data !== "object") return undefined;

    if (isApiErrorResponse(data)) {
      return data.error.message;
    }

    const d = data as Record<string, unknown>;
    if (typeof d.message === "string") {
      return d.message;
    }

    return undefined;
  }

  private static isAxiosError(error: unknown): error is AxiosError {
    return (
      typeof error === "object" &&
      error !== null &&
      "isAxiosError" in error &&
      (error as any).isAxiosError === true
    );
  }
}
